import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn } from '../firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { useTranslation } from '../hooks/useTranslation';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { t } = useTranslation();
  
  // Refs for auto-focus
  const emailRef = useRef(null);

  // Redirect if user is already authenticated
  useEffect(() => {
    if (user) {
      const from = location.state?.from?.pathname || '/dashboard';
      // If the user was trying to access a list, redirect to the full view
      if (from.startsWith('/list/') && !from.endsWith('/view')) {
        navigate(`${from}/view`, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [user, navigate, location.state]);

  // Auto-focus email field on mobile
  useEffect(() => {
    if (emailRef.current && window.innerWidth < 640) {
      setTimeout(() => emailRef.current.focus(), 100);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(value) ? null : t('login.invalidEmail');
      case 'password':
        return value.length >= 6 ? null : t('login.passwordTooShort');
      default:
        return null;
    }
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    setFocusedField(null);
    
    if (value.trim()) {
      const error = validateField(name, value);
      if (error) {
        setFieldErrors(prev => ({
          ...prev,
          [name]: error
        }));
      }
    }
  };

  const handleFieldFocus = (e) => {
    setFocusedField(e.target.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all fields
    const errors = {};
    const emailError = validateField('email', formData.email);
    const passwordError = validateField('password', formData.password);
    
    if (emailError) errors.email = emailError;
    if (passwordError) errors.password = passwordError;
    
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    
    setLoading(true);

    const result = await signIn(formData.email, formData.password);
    
    if (result.success) {
      toast.success(t('login.welcomeBack'));
      // Redirect immediately after successful login
      const from = location.state?.from?.pathname || '/dashboard';
      // If the user was trying to access a list, redirect to the full view
      if (from.startsWith('/list/') && !from.endsWith('/view')) {
        navigate(`${from}/view`, { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  // Show loading while checking authentication
  if (!user && loading) {
    return <LoadingSpinner text={t('login.signingYouIn')} />;
  }

  // Don't render the form if user is authenticated (they should be redirected)
  if (user) {
    return <LoadingSpinner text={t('login.redirecting')} />;
  }

  return (
    <div className="min-h-[calc(100vh-6rem)] flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h2 className="mt-6 text-2xl sm:text-3xl font-bold text-gray-900">
            {t('login.title')}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {t('login.or')}{' '}
            <Link
              to="/register"
              state={{ from: location.state?.from }}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              {t('login.createAccount')}
            </Link>
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                {t('login.emailAddress')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className={`h-5 w-5 ${fieldErrors.email ? 'text-red-400' : focusedField === 'email' ? 'text-primary-500' : 'text-gray-400'}`} />
                </div>
                <input
                  ref={emailRef}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className={`input-field pl-10 pr-10 ${fieldErrors.email ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : focusedField === 'email' ? 'border-primary-300 focus:ring-primary-500 focus:border-primary-500' : ''}`}
                  placeholder={t('login.enterEmail')}
                  value={formData.email}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  onBlur={handleFieldBlur}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  {fieldErrors.email ? (
                    <AlertCircle className="h-5 w-5 text-red-400" />
                  ) : formData.email && !fieldErrors.email && validateField('email', formData.email) === null ? (
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  ) : null}
                </div>
              </div>
              {fieldErrors.email && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {fieldErrors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                {t('login.password')}
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className={`h-5 w-5 ${fieldErrors.password ? 'text-red-400' : focusedField === 'password' ? 'text-primary-500' : 'text-gray-400'}`} />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className={`input-field pl-10 pr-12 ${fieldErrors.password ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : focusedField === 'password' ? 'border-primary-300 focus:ring-primary-500 focus:border-primary-500' : ''}`}
                  placeholder={t('login.enterPassword')}
                  value={formData.password}
                  onChange={handleChange}
                  onFocus={handleFieldFocus}
                  onBlur={handleFieldBlur}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <button
                    type="button"
                    className="p-1 hover:bg-gray-100 rounded transition-colors"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {formData.password && !fieldErrors.password && validateField('password', formData.password) === null && (
                  <div className="absolute inset-y-0 right-0 pr-10 flex items-center pointer-events-none">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                )}
              </div>
              {fieldErrors.password && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {fieldErrors.password}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Link
              to="/forgot-password"
              className="text-sm text-primary-600 hover:text-primary-500"
            >
              {t('login.forgotPassword')}
            </Link>
          </div>

          <div>
            <button
              type="submit"
              className="w-full btn-primary py-3 text-base"
              disabled={loading}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
